import { isAddress } from 'viem';
import { computeMerkleRoot } from './merkltree';
import {
  CampaignResult,
  RawInputData,
  processRawInputData,
  normalizeAddress,
  CampaignCode,
  ClaimedStatusByCampaign,
  ALL_CAMPAIGN_CODES,
} from './types';

const rawRoadToVeloraData = require('../data/campaign-data-road-to-velora.json');
const banklessData = require('../data/campaign-data-bankless.json');

interface UserParticipation {
  campaign: {
    week: number;
    campaign: string;
    code: CampaignCode;
    contract_address: `0x${string}`;
    chain_id: number;
  };
  amount: bigint;
  proofs: string[];
}

// Process raw JSON data into typed, normalized data
const RAW_CAMPAIGNS = [rawRoadToVeloraData, banklessData].map((input) =>
  processRawInputData(input as RawInputData),
);
const CAMPAIGNS_WITH_MERKLE_DATA: CampaignResult[] = RAW_CAMPAIGNS.map((campaign) =>
  computeMerkleRoot(campaign),
);

// validate CAMPAIGNS_WITH_MERKLE_DATA
function validateCampaigns(campaigns: CampaignResult[]) {
  // check if each campaign has a unique code
  const codes = campaigns.map((c) => c.root.week_to_campaign_mapping.code);
  const uniqueCodes = new Set(codes);
  if (codes.length !== uniqueCodes.size) {
    const duplicated = codes.filter((code, index) => codes.indexOf(code) !== index);
    // console.error('Duplicated campaign codes:', duplicated);
    throw new Error('Duplicate campaign codes found in campaign data ' + duplicated.join(', '));
  }

  // each campaign must have a distro file
  ALL_CAMPAIGN_CODES.forEach((code) => {
    if (!codes.includes(code)) {
      throw new Error(`Campaign code ${code} is missing in campaign data`);
    }
  });

  // throw error if unexcepted campaign codes are found
  codes.forEach((code) => {
    if (!ALL_CAMPAIGN_CODES.includes(code)) {
      throw new Error(`Unexpected campaign code found in campaign data: ${code}`);
    }
  });
}
validateCampaigns(CAMPAIGNS_WITH_MERKLE_DATA);

function getCampaigns(): CampaignResult[] {
  return CAMPAIGNS_WITH_MERKLE_DATA;
}

const DISTRIBUTED_CAMPAIGN_CODES: Set<CampaignCode> = new Set(
  CAMPAIGNS_WITH_MERKLE_DATA.map((c) => c.root.week_to_campaign_mapping.code),
);

function getCampaignByWeek(week: number): CampaignResult | undefined {
  return getCampaigns().find((c) => c.root.week_to_campaign_mapping.week === week);
}
function getCampaignByCode(code: string): CampaignResult | undefined {
  return getCampaigns().find((c) => c.root.week_to_campaign_mapping.code === code);
}

function getRawCampaignByCode(code: CampaignCode): RawInputData | undefined {
  return RAW_CAMPAIGNS.find((c) => c.week_to_campaign_mapping.code === code);
}

function getUserEarnedByCampaign(user: string): { [campaignCode: string]: bigint } {
  const result = getCampaigns().reduce<{ [campaignCode: string]: bigint }>((acc, campaign) => {
    const campaignCode = campaign.root.week_to_campaign_mapping.code;

    const entry = campaign.participants_with_proofs.find(
      (e) => e.participant === normalizeAddress(user),
    );
    if (entry) {
      acc[campaignCode] = BigInt(entry.amount);
    }
    return acc;
  }, {});
  return result;
}

const getUserTotalClaimableVlrWei = (
  address: string,
  userClaimedByCampaignCode: ClaimedStatusByCampaign,
): bigint => {
  const sum = getCampaigns().reduce((acc, campaign) => {
    const campaignCode = campaign.root.week_to_campaign_mapping.code;
    // Skip campaigns that the user has already claimed from
    if (userClaimedByCampaignCode[campaignCode]) {
      return acc;
    }

    const entry = campaign.participants_with_proofs.find(
      (e) => e.participant === normalizeAddress(address),
    );
    if (entry) {
      return acc + BigInt(entry.amount);
    }
    return acc;
  }, BigInt(0));

  console.log('Total claimable VLR (wei):', sum.toString(), { userClaimedByCampaignCode });
  return sum;
};

const getUserParticipations = (
  address: string,
  campaignCode: CampaignCode,
): UserParticipation[] => {
  return getCampaigns()
    .filter((campaign) => {
      if (campaignCode && campaign.root.week_to_campaign_mapping.code !== campaignCode) {
        return false;
      }
      return true;
    })
    .map((campaign) => {
      const entry = campaign.participants_with_proofs.find(
        (e) => e.participant === normalizeAddress(address),
      );
      //       interface UserParticipation {
      //   campaign: {
      //     week: number;
      //     campaign: string;
      //     code: CampaignCode;
      //     contract_address: `0x${string}`;
      //     chain_id: number;
      //   };
      //   amount: bigint;
      //   proofs: string[];
      // }

      if (!entry) return null;

      return {
        campaign: {
          week: campaign.root.week_to_campaign_mapping.week,
          campaign: campaign.root.week_to_campaign_mapping.campaign,
          code: campaign.root.week_to_campaign_mapping.code,
          contract_address: campaign.contract_data.claimer_contract as `0x${string}`,
          chain_id: campaign.contract_data.chain_id,
        },
        amount: entry ? BigInt(entry.amount) : BigInt(0),
        proofs: entry ? entry.merkleProofs : [],
      };
    })
    .filter((v) => v !== null);
};

export function getClaimerContractAddress(): `0x${string}` {
  const uniqueAddress = new Set(getCampaigns().map((c) => c.contract_data.claimer_contract));
  if (!uniqueAddress.size) {
    throw new Error('No claimer contract address found in campaign data');
  }
  if (uniqueAddress.size > 1) {
    throw new Error('Multiple different claimer contract addresses found in campaign data');
  }

  const address = uniqueAddress.values().next().value;
  if (!address) {
    throw new Error('Claimer contract address is undefined');
  }
  if (!isAddress(address)) {
    throw new Error(`Claimer contract address is not a valid address: ${address}`);
  }
  return address;
}

export const MAX_WEEK_ON_CLAIMER = getCampaigns().reduce(
  (max, c) => Math.max(max, c.root.week_to_campaign_mapping.week),
  0,
);

function getVlrTokenAddress(): `0x${string}` {
  const uniqueAddress = new Set(getCampaigns().map((c) => c.contract_data.token));
  if (!uniqueAddress.size) {
    throw new Error('No VLR token address found in campaign data');
  }
  if (uniqueAddress.size > 1) {
    throw new Error('Multiple different VLR token addresses found in campaign data');
  }

  const address = uniqueAddress.values().next().value;
  if (!address) {
    throw new Error('VLR token address is undefined');
  }
  if (!isAddress(address)) {
    throw new Error(`VLR token address is not a valid address: ${address}`);
  }
  return address;
}

export {
  getRawCampaignByCode,
  getCampaignByCode,
  getUserTotalClaimableVlrWei,
  getUserParticipations,
  getCampaigns,
  getCampaignByWeek,
  getVlrTokenAddress,
  getUserEarnedByCampaign,
  DISTRIBUTED_CAMPAIGN_CODES,
};
export type { UserParticipation };
